import { checkWorkflow, fetchWorkflowStartSchema, publishWorkflow } from "@/actions/workflows/actions";
import { syncGailWebhooksForWorkflow } from "@/actions/webhooks/actions";
import { deleteWorkflowToolsForWorkflow, syncWorkflowTool } from "@/actions/tools/workflowToolSync";
import { attachToolToAssistant } from "@/actions/settings/assistants/actions";
import { GAIL_WORKFLOWS_API } from "@/utils/common/constants/routes";
import { getStartNodeMetadata, getStartToolMetadata } from "@/utils/workflows/startNodeMetadata";
import type { WebhookEventType } from "@/enums/settings";
import type { StartNodeMetadata, Workflow, WorkflowToolStartMode } from "@/types/workflows";

/** How the Start node asks to be registered as a Galactus tool. */
export type WorkflowToolIntent = {
  exposeAsTool: boolean;
  hasInputSchema: boolean;
  toolId: string | null;
  toolStartMode: WorkflowToolStartMode;
};

export type SideEffectStatus = "synced" | "failed" | "skipped";

export type WorkflowToolOutcome =
  | { status: "synced" | "removed" | "skipped"; toolId: string | null }
  | { status: "failed"; toolId: string | null; operation: "sync" | "delete" };

export type WorkflowPublication =
  | {
      status: "check_failed";
      error?: string;
      errorDetails: NonNullable<Awaited<ReturnType<typeof checkWorkflow>>["errorDetails"]>;
    }
  | { status: "publish_failed"; error?: string }
  | {
      status: "published";
      workflow: Workflow;
      webhooks: SideEffectStatus;
      tool: WorkflowToolOutcome;
      attachment: SideEffectStatus;
    };

export type PublishWorkflowParams = {
  workflow: Workflow;
  tool: WorkflowToolIntent;
  /** `null` leaves existing Gail webhook subscriptions untouched. */
  gailWebhookEvents: WebhookEventType[] | null;
  /** Runs as soon as Omega publishes, before the registrations. */
  onPublished?: (published: Workflow) => void;
  /** Records a new or removed tool id on the draft after publication. */
  persistToolId: (toolId: string | null, published: Workflow) => Promise<void>;
  /** Attach a synced tool to this assistant; omit to leave assistants unchanged. */
  attachToAssistantId?: string;
};

/**
 * The single publish path for a workflow: Omega check and publish, then the
 * product registrations Omega does not own (Gail webhook subscriptions, the
 * Galactus tool, and assistant attachment). Registration failures never undo
 * a publication, so they are reported individually.
 */
export async function publishWorkflowWithSideEffects(
  params: PublishWorkflowParams
): Promise<WorkflowPublication> {
  const payload = publishPayload(params.workflow);
  const check = await checkWorkflow(payload);
  if (!check.data) {
    return { status: "check_failed", error: check.error, errorDetails: check.errorDetails ?? [] };
  }

  const publication = await publishWorkflow(payload);
  const published = publication.data;
  if (!published) return { status: "publish_failed", error: publication.error };
  params.onPublished?.(published);

  const webhooks = await syncWebhooks(published.id, params.gailWebhookEvents);
  const tool = await syncTool(published, params.tool, params.persistToolId);
  const attachment = await attachTool(tool, params.attachToAssistantId);
  return { status: "published", workflow: published, webhooks, tool, attachment };
}

/** `workflows` translation keys for the registrations a publication could not complete. */
export function publicationFailureKeys(
  publication: Extract<WorkflowPublication, { status: "published" }>
): string[] {
  return [
    ...(publication.webhooks === "failed" ? ["startSettings.gailWebhooksSyncFailed"] : []),
    ...(publication.tool.status === "failed"
      ? [publication.tool.operation === "sync" ? "startSettings.toolSyncFailed" : "startSettings.toolDeleteFailed"]
      : []),
    ...(publication.attachment === "failed" ? ["alerts.attachToolError"] : []),
  ];
}

/** Tool intent and webhook events recorded on a saved graph's Start node. */
export function workflowRegistrationsFromGraph(graph: Workflow["graph"]): {
  tool: WorkflowToolIntent;
  gailWebhookEvents: WebhookEventType[] | null;
} {
  const metadata = getStartNodeMetadata(graph) as StartNodeMetadata | undefined;
  const start = graph.nodes.find((node) => node.type === "start");
  const { toolId, exposeAsTool } = getStartToolMetadata(graph);
  return {
    tool: {
      exposeAsTool: exposeAsTool === true,
      hasInputSchema: start?.type === "start" && start.inputSchema != null,
      toolId,
      toolStartMode: metadata?.toolStartMode === "async" ? "async" : "sync",
    },
    gailWebhookEvents: Array.isArray(metadata?.gailWebhookEvents) ? metadata.gailWebhookEvents : null,
  };
}

/** A copy of the graph whose Start node records `toolId`, or no tool id for `null`. */
export function withStartToolId(graph: Workflow["graph"], toolId: string | null): Workflow["graph"] {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (node.type !== "start") return node;
      const { toolId: _previous, ...metadata } = (node.metadata ?? {}) as StartNodeMetadata;
      const next = toolId ? { ...metadata, toolId } : metadata;
      return { ...node, metadata: next };
    }),
  };
}

// Omega defaults a first publication to revision 1, so revision 1 is omitted.
function publishPayload(workflow: Workflow): Workflow {
  const { revision, ...rest } = workflow;
  return revision && revision > 1 ? { ...rest, revision } : rest;
}

async function syncWebhooks(
  workflowId: string,
  events: WebhookEventType[] | null
): Promise<SideEffectStatus> {
  if (events === null) return "skipped";
  try {
    await syncGailWebhooksForWorkflow(workflowId, events);
    return "synced";
  } catch {
    return "failed";
  }
}

type ToolRegistration = {
  action: "sync" | "delete" | "skip";
  toolId: string | null;
};

/**
 * Whether this publication creates, removes, or leaves the Galactus tool.
 * The live intent can lag the graph Omega just published, so a missing tool id
 * is filled from the published Start node, and a delete requires both sources
 * to agree the workflow is not a tool.
 */
function chooseToolRegistration(
  intent: WorkflowToolIntent,
  publishedStart: { toolId: string | null; exposeAsTool: boolean | null }
): ToolRegistration {
  const toolId = intent.toolId ?? publishedStart.toolId;

  if (intent.exposeAsTool && intent.hasInputSchema) {
    return { action: "sync", toolId };
  }

  // Deleting removes every tool pinned to this workflow and detaches it from
  // every assistant. A bare legacy tool id counts as still exposed.
  if (intent.exposeAsTool || publishedStart.exposeAsTool === true) {
    return { action: "skip", toolId };
  }

  // An explicit opt-out can leave a tool this graph no longer names, so that
  // case still looks the workflow up.
  if (toolId !== null || publishedStart.exposeAsTool === false) {
    return { action: "delete", toolId };
  }

  // No id, and the published node never mentioned a tool: nothing to remove.
  return { action: "skip", toolId };
}

async function syncTool(
  published: Workflow,
  intent: WorkflowToolIntent,
  persistToolId: PublishWorkflowParams["persistToolId"]
): Promise<WorkflowToolOutcome> {
  const registration = chooseToolRegistration(intent, getStartToolMetadata(published.graph));

  switch (registration.action) {
    case "sync":
      return createOrUpdateTool(published, { ...intent, toolId: registration.toolId }, persistToolId);
    case "skip":
      return { status: "skipped", toolId: registration.toolId };
    case "delete":
      return deleteTool(published, registration.toolId, persistToolId);
    default: {
      const unreachable: never = registration.action;
      return unreachable;
    }
  }
}

async function deleteTool(
  published: Workflow,
  toolId: string | null,
  persistToolId: PublishWorkflowParams["persistToolId"]
): Promise<WorkflowToolOutcome> {
  try {
    const { deletedToolIds, error } = await deleteWorkflowToolsForWorkflow({
      workflowId: published.id,
      toolId,
    });
    if (error) return { status: "failed", toolId, operation: "delete" };
    // An opt-out with no tool behind it has nothing to report as removed.
    if (deletedToolIds.length === 0 && !toolId) return { status: "skipped", toolId: null };
    // An id inherited from another workflow is not deleted, but still leaves this graph.
    await persistToolId(null, published);
    return { status: "removed", toolId: null };
  } catch {
    return { status: "failed", toolId, operation: "delete" };
  }
}

async function createOrUpdateTool(
  published: Workflow,
  intent: WorkflowToolIntent,
  persistToolId: PublishWorkflowParams["persistToolId"]
): Promise<WorkflowToolOutcome> {
  const failed = { status: "failed", toolId: intent.toolId, operation: "sync" } as const;
  try {
    const openApiSpec = await fetchWorkflowStartSchema(published.id, {
      startMode: intent.toolStartMode,
    });
    if (!openApiSpec) return failed;

    const result = await syncWorkflowTool({
      workflowId: published.id,
      toolId: intent.toolId,
      name: published.name,
      description: published.description || `Workflow tool for ${published.name}`,
      openApiSpec,
      serverUrl: new URL(GAIL_WORKFLOWS_API).origin,
    });
    if (result.error) return failed;

    const toolId = result.toolId ?? intent.toolId;
    if (toolId !== intent.toolId) await persistToolId(toolId, published);
    return { status: "synced", toolId };
  } catch {
    return failed;
  }
}

async function attachTool(
  tool: WorkflowToolOutcome,
  assistantId: string | undefined
): Promise<SideEffectStatus> {
  if (!assistantId || tool.status !== "synced" || !tool.toolId) return "skipped";
  try {
    const result = await attachToolToAssistant(tool.toolId, assistantId);
    return result.error ? "failed" : "synced";
  } catch {
    return "failed";
  }
}
